import { clsx } from "clsx";
import { useRef, useState } from "react";
import type { JSX } from "react/jsx-runtime";
import CategorySelection from "./CategorySelection/CategorySelection";
import type { HeaderCategory } from "./HeaderCategory.d.ts";
import KeywordSelection from "./KeywordSelection/KeywordSelection";
import LocationSelection from "./LocationSelection/LocationSelection";
import RadiusSelection from "./RadiusSelection/RadiusSelection";

type Props = {
  categories: HeaderCategory[];
  algoliaAppId: string;
  algoliaApiKey: string;
  searchUrl: string;
  locationSuggestionsUrl: string;
  locationName?: string;
  distanceRadius?: number;
};

function SearchForm({
  categories,
  algoliaApiKey,
  algoliaAppId,
  searchUrl,
  locationSuggestionsUrl,
  locationName = "",
  distanceRadius = 0,
}: Props): JSX.Element {
  const formRef = useRef<HTMLFormElement>(null);
  const [categoryId, setCategoryId] = useState<number>();
  const [locationQuery, setLocationQuery] = useState(locationName);

  function handleFormSubmit(): void {
    if (formRef.current) {
      formRef.current.submit();
    }
  }

  return (
    <form
      ref={formRef}
      action={searchUrl}
      className="grid h-xxxlarge max-w-full grid-cols-[2fr_1px_1.3fr_1px_1.3fr_1px_1fr_auto] items-center rounded-full bg-background text-onSurfaceNonessential"
    >
      <SearchFormSectionWrapper className="flex items-center rounded-l-full">
        <KeywordSelection
          onFormSubmit={handleFormSubmit}
          setCategoryId={setCategoryId}
          categoryId={categoryId}
          algoliaAppId={algoliaAppId}
          algoliaApiKey={algoliaApiKey}
        />
      </SearchFormSectionWrapper>
      <SearchFormDivider />
      <SearchFormSectionWrapper className="min-w-none">
        <CategorySelection
          categories={categories}
          onFormSubmit={handleFormSubmit}
          categoryId={categoryId}
          setCategoryId={setCategoryId}
        />
      </SearchFormSectionWrapper>
      <SearchFormDivider />
      <SearchFormSectionWrapper className="flex items-center">
        <LocationSelection
          onFormSubmit={handleFormSubmit}
          locationSuggestionsUrl={locationSuggestionsUrl}
          setLocationQuery={setLocationQuery}
          locationQuery={locationQuery}
        />
      </SearchFormSectionWrapper>
      <SearchFormDivider />
      <SearchFormSectionWrapper className="min-w-none">
        <RadiusSelection
          initialRadius={distanceRadius}
          onFormSubmit={handleFormSubmit}
          disabled={locationQuery.length <= 0}
        />
      </SearchFormSectionWrapper>
      <button className="mx-xsmall flex h-xlarge items-center rounded-full bg-interactive px-medium font-strong text-onInteractive hover:bg-interactiveContainer">
        Finden
      </button>
    </form>
  );
}

function SearchFormSectionWrapper({
  className,
  children,
}: {
  className?: string;
  children: JSX.Element;
}): JSX.Element {
  return (
    <div
      className={clsx(
        className,
        "size-full focus-within:z-raised focus-within:outline focus-within:outline-1 focus-within:outline-onSurface",
      )}
    >
      {children}
    </div>
  );
}

function SearchFormDivider(): JSX.Element {
  return <div className="h-xlarge w-[1px] flex-none bg-utilitySubdued" />;
}

export default SearchForm;
